import { useCallback, useEffect, useMemo, useState } from 'react';
import type { IconType } from 'react-icons';
import {
  FiActivity,
  FiAlertTriangle,
  FiArrowUpRight,
  FiBattery,
  FiBell,
  FiCalendar,
  FiCheckCircle,
  FiChevronRight,
  FiClock,
  FiFileText,
  FiMapPin,
  FiRefreshCw,
  FiShield,
  FiUserCheck,
  FiUsers,
} from 'react-icons/fi';
import { commandService, type CommandSnapshot } from '../../services/commandService';
import type { Alarm } from '../../services/alarmService';
import type { AppNotification } from '../../services/notificationService';
import type { ReportListItem } from '../../services/reportService';
import { useI18n } from '../../i18n';
import './CommandHomePage.css';

interface CommandHomePageProps {
  activeAlarms: Alarm[];
  alarmsReady?: boolean;
  alarmsAvailable?: boolean;
  notifications: AppNotification[];
  unreadCount: number;
  notificationsReady?: boolean;
  notificationsAvailable?: boolean;
  onOpenAlarms: () => void;
  onOpenDutyBoard: (target: { siteId: string | null; date: string; dutyId?: string | null }) => void;
  onOpenLeave: () => void;
  onOpenLicence: (licenceId: string) => void;
  onOpenReport: (reportId: string) => void;
  onOpenReports: (category: 'incident' | 'summary') => void;
  onOpenNotification: (notification: AppNotification) => void;
  onMarkNotificationRead: (ids: string[]) => void | Promise<void>;
  onMarkAllNotificationsRead?: () => void | Promise<void>;
  onOpenLiveView: () => void;
}

type CommandTone = 'danger' | 'warning' | 'info' | 'success' | 'neutral';

interface MetricCardProps {
  icon: IconType;
  label: string;
  value: string | number;
  detail: string;
  tone: CommandTone;
  onClick?: () => void;
}

const EMPTY_SNAPSHOT: CommandSnapshot = {
  operations: null,
  pendingLeaveRequests: [],
  incidentReports: {
    reports: [],
    totalCount: 0,
    statusCounts: { open: 0, submitted: 0, in_review: 0, needs_more_info: 0, escalated: 0, closed: 0 },
  },
  summaryReports: {
    reports: [],
    totalCount: 0,
    statusCounts: { open: 0, submitted: 0, in_review: 0, needs_more_info: 0, escalated: 0, closed: 0 },
  },
  licenceWarnings: [],
  licenceWarningCount: undefined,
  unavailableSections: [],
  generatedAt: '',
};

function MetricCard({ icon: Icon, label, value, detail, tone, onClick }: MetricCardProps) {
  const content = (
    <>
      <span className={`command-metric__icon command-tone--${tone}`}><Icon aria-hidden="true" /></span>
      <span className="command-metric__copy">
        <span className="command-metric__label">{label}</span>
        <strong className="command-metric__value">{value}</strong>
        <span className="command-metric__detail">{detail}</span>
      </span>
      {onClick && <FiChevronRight className="command-metric__arrow" aria-hidden="true" />}
    </>
  );

  if (!onClick) return <div className={`command-metric command-metric--${tone}`}>{content}</div>;

  return (
    <button type="button" className={`command-metric command-metric--${tone}`} onClick={onClick}>
      {content}
    </button>
  );
}

function PanelHeader({
  eyebrow,
  title,
  detail,
  actionLabel,
  onAction,
}: {
  eyebrow: string;
  title: string;
  detail?: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="command-panel__header">
      <div>
        <div className="command-panel__eyebrow">{eyebrow}</div>
        <h2 className="command-panel__title">{title}</h2>
        {detail && <p className="command-panel__detail">{detail}</p>}
      </div>
      {actionLabel && onAction && (
        <button type="button" className="command-link-button" onClick={onAction}>
          {actionLabel}<FiArrowUpRight aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

function formatMinute(value: number): string {
  const normalized = ((value % 1440) + 1440) % 1440;
  return `${String(Math.floor(normalized / 60)).padStart(2, '0')}:${String(normalized % 60).padStart(2, '0')}`;
}

export default function CommandHomePage({
  activeAlarms,
  alarmsReady = true,
  alarmsAvailable = true,
  notifications,
  unreadCount,
  notificationsReady = true,
  notificationsAvailable = true,
  onOpenAlarms,
  onOpenDutyBoard,
  onOpenLeave,
  onOpenLicence,
  onOpenReport,
  onOpenReports,
  onOpenNotification,
  onMarkNotificationRead,
  onMarkAllNotificationsRead,
  onOpenLiveView,
}: CommandHomePageProps) {
  const { locale, t, tn } = useI18n();
  const [snapshot, setSnapshot] = useState<CommandSnapshot>(EMPTY_SNAPSHOT);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadSnapshot = useCallback(async () => {
    setIsLoading(true);
    try {
      const nextSnapshot = await commandService.fetchSnapshot(refreshKey);
      setSnapshot(nextSnapshot);
    } catch (error) {
      // The service normally returns partial data; this catch protects the
      // page from unexpected auth/network failures outside those feeds.
      console.error('Could not load command data:', (error as Error).message);
      // Preserve last-known-good values during an unexpected refresh failure.
      setSnapshot((current) => ({
        ...current,
        unavailableSections: ['operations', 'leave', 'reports', 'licences'],
        generatedAt: new Date().toISOString(),
      }));
    } finally {
      setIsLoading(false);
    }
  }, [refreshKey]);

  useEffect(() => {
    void loadSnapshot();
  }, [loadSnapshot]);

  // Realtime hooks keep alarms and notifications fresh. Slower planning and
  // compliance feeds are refreshed every five minutes, plus on demand.
  useEffect(() => {
    const interval = window.setInterval(() => setRefreshKey((key) => key + 1), 300_000);
    return () => window.clearInterval(interval);
  }, []);

  const operations = snapshot.operations;
  const snapshotReady = Boolean(snapshot.generatedAt);
  const unavailableSections = useMemo(
    () => new Set(snapshot.unavailableSections),
    [snapshot.unavailableSections],
  );
  const operationsAvailable = snapshotReady && Boolean(operations) && !unavailableSections.has('operations');
  const leaveAvailable = snapshotReady && !unavailableSections.has('leave');
  const reportsAvailable = snapshotReady && !unavailableSections.has('reports');
  const licencesAvailable = snapshotReady && !unavailableSections.has('licences');
  const alarmFeedAvailable = alarmsReady && alarmsAvailable;
  const notificationFeedAvailable = notificationsReady && notificationsAvailable;
  const noShows = operations?.noShows ?? [];
  const coverageGaps = operations?.upcomingGaps ?? [];
  const pendingLeave = snapshot.pendingLeaveRequests;
  const displayedAlarms = activeAlarms.slice(0, 5);
  const displayedNoShows = noShows.slice(0, 5);
  const displayedLeave = pendingLeave.slice(0, 5);
  const noShowCount = operationsAvailable ? (operations?.noShowCount ?? noShows.length) : null;
  const coverageIssueCount = operationsAvailable ? (operations?.coverageIssueCount ?? coverageGaps.length) : null;
  const openPositions = operationsAvailable
    ? (operations?.openPositionCount ?? coverageGaps.reduce((total, gap) => total + Math.max(gap.openPositions, 0), 0))
    : null;
  const licenceWarningCount = licencesAvailable
    ? (snapshot.licenceWarningCount ?? snapshot.licenceWarnings.length)
    : null;
  const recentNotifications = notifications.slice(0, 5);
  const firstUnreadNotification = notifications.find((notification) => !notification.read_at);

  const reportReviewCount = useMemo(() => {
    if (!reportsAvailable) return null;
    // `open` is the report service's aggregate of every non-closed status.
    return snapshot.incidentReports.statusCounts.open + snapshot.summaryReports.statusCounts.open;
  }, [reportsAvailable, snapshot.incidentReports.statusCounts.open, snapshot.summaryReports.statusCounts.open]);

  const priorityCount = (alarmFeedAvailable ? activeAlarms.length : 0)
    + (noShowCount ?? 0)
    + (leaveAvailable ? pendingLeave.length : 0)
    + (coverageIssueCount ?? 0)
    + (licenceWarningCount ?? 0)
    + (reportReviewCount ?? 0)
    + (notificationFeedAvailable ? unreadCount : 0);
  const allPriorityFeedsAvailable = alarmFeedAvailable
    && operationsAvailable
    && leaveAvailable
    && reportsAvailable
    && licencesAvailable
    && notificationFeedAvailable;

  const formatDate = (value: string, options?: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...options,
  }).format(new Date(`${value}T12:00:00`));

  const formatDateTime = (value: string) => new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));

  const formatRelative = (value: string) => {
    const deltaMinutes = Math.round((new Date(value).getTime() - Date.now()) / 60_000);
    const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (Math.abs(deltaMinutes) < 60) return relative.format(deltaMinutes, 'minute');
    const deltaHours = Math.round(deltaMinutes / 60);
    if (Math.abs(deltaHours) < 24) return relative.format(deltaHours, 'hour');
    return relative.format(Math.round(deltaHours / 24), 'day');
  };

  const openNotification = (notification: AppNotification) => {
    if (!notification.read_at) void onMarkNotificationRead([notification.id]);
    onOpenNotification(notification);
  };

  const getReportStatus = (status: string) => {
    switch (status) {
      case 'submitted': return t('Submitted');
      case 'in_review': return t('In review');
      case 'needs_more_info': return t('Needs more info');
      case 'escalated': return t('Escalated');
      case 'closed': return t('Closed');
      default: return t('Open||report');
    }
  };

  const getDutyName = (name: string, kind: string) => {
    if (name.trim()) return name;
    switch (kind) {
      case 'fixed_post': return t('Fixed Post');
      case 'patrol': return t('Patrol');
      case 'desk': return t('Desk');
      case 'mobile': return t('Mobile');
      default: return t('Custom');
    }
  };

  const renderReport = (report: ReportListItem, category: 'incident' | 'summary') => {
    const updatedAt = report.status_updated_at || report.created_at;
    return (
      <button
        type="button"
        className="command-report-row"
        key={report.id}
        onClick={() => onOpenReport(report.id)}
      >
        <span className={`command-report-row__icon command-report-row__icon--${category}`}>
          <FiFileText aria-hidden="true" />
        </span>
        <span className="command-report-row__copy">
          <span className="command-report-row__topline">
            <strong>{report.title?.trim() || (category === 'incident' ? t('Untitled incident') : t('Untitled summary'))}</strong>
            <span className={`command-status command-status--${report.status}`}>{getReportStatus(report.status)}</span>
          </span>
          <span className="command-report-row__meta">
            {report.sites?.name || t('No site')} · {formatRelative(updatedAt)}
          </span>
        </span>
        <FiChevronRight className="command-row__chevron" aria-hidden="true" />
      </button>
    );
  };

  return (
    <div className="command-home">
      <section className="command-hero" data-tour="command-overview">
        <div className="command-hero__copy">
          <div className="command-hero__eyebrow"><span className="command-live-dot" />{t('Operational command')}</div>
          <h1>{t('Ops Centre')}</h1>
          <p>{t('Everything that needs your attention, in one place.')}</p>
        </div>
        <div className="command-hero__actions">
          {snapshot.generatedAt && (
            <span className="command-updated">
              {t(snapshot.unavailableSections.length ? 'Refresh attempted {time}' : 'Updated {time}', {
                time: formatDateTime(snapshot.generatedAt),
              })}
            </span>
          )}
          <button
            type="button"
            className="btn btn--secondary command-refresh"
            onClick={() => setRefreshKey((key) => key + 1)}
            disabled={isLoading}
          >
            <FiRefreshCw className={isLoading ? 'command-refresh__icon--spinning' : ''} aria-hidden="true" />
            {t('Refresh')}
          </button>
        </div>
      </section>

      {(snapshot.unavailableSections.length > 0
        || (alarmsReady && !alarmsAvailable)
        || (notificationsReady && !notificationsAvailable)) && (
        <div className="command-data-warning" role="status">
          <FiAlertTriangle aria-hidden="true" />
          <span>{t('Some command data could not be refreshed. Last available values remain visible.')}</span>
        </div>
      )}

      <section className="command-metrics" aria-label={t('Ops Centre summary')}>
        <MetricCard
          icon={FiAlertTriangle}
          label={t('Active alarms')}
          value={alarmFeedAvailable ? activeAlarms.length : '—'}
          detail={!alarmsReady ? t('Checking live alarms') : !alarmsAvailable ? t('Data unavailable') : activeAlarms.length ? t('Open response queue') : t('All guards reporting safe')}
          tone={!alarmFeedAvailable ? 'neutral' : activeAlarms.length ? 'danger' : 'success'}
          onClick={alarmFeedAvailable && activeAlarms.length ? onOpenAlarms : undefined}
        />
        <MetricCard
          icon={FiClock}
          label={t('No-shows')}
          value={noShowCount ?? '—'}
          detail={operationsAvailable ? t('Published shifts not started') : t('Data unavailable')}
          tone={noShowCount ? 'danger' : 'neutral'}
          onClick={noShows[0] ? () => onOpenDutyBoard({ siteId: noShows[0].siteId, date: noShows[0].slotDate, dutyId: noShows[0].dutyId }) : undefined}
        />
        <MetricCard
          icon={FiCalendar}
          label={t('Coverage gaps')}
          value={coverageIssueCount ?? '—'}
          detail={openPositions == null ? t('Data unavailable') : tn('{count} open position', '{count} open positions', openPositions)}
          tone={coverageIssueCount ? 'warning' : 'neutral'}
          onClick={coverageGaps[0] ? () => onOpenDutyBoard({ siteId: coverageGaps[0].siteId, date: coverageGaps[0].slotDate, dutyId: coverageGaps[0].dutyId }) : undefined}
        />
        <MetricCard
          icon={FiUserCheck}
          label={t('Pending leave')}
          value={leaveAvailable ? pendingLeave.length : '—'}
          detail={leaveAvailable ? t('Requests awaiting review') : t('Data unavailable')}
          tone={leaveAvailable && pendingLeave.length ? 'warning' : 'neutral'}
          onClick={leaveAvailable && pendingLeave.length ? onOpenLeave : undefined}
        />
        <MetricCard
          icon={FiShield}
          label={t('Licence warnings')}
          value={licenceWarningCount ?? '—'}
          detail={licencesAvailable ? t('Expired or due within 30 days') : t('Data unavailable')}
          tone={licenceWarningCount ? 'warning' : 'neutral'}
          onClick={snapshot.licenceWarnings[0] ? () => onOpenLicence(snapshot.licenceWarnings[0].id) : undefined}
        />
        <MetricCard
          icon={FiFileText}
          label={t('Reports to review')}
          value={reportReviewCount ?? '—'}
          detail={reportsAvailable ? t('New and updated field reports') : t('Data unavailable')}
          tone={reportReviewCount ? 'info' : 'neutral'}
          onClick={reportReviewCount ? () => onOpenReports(snapshot.incidentReports.statusCounts.open ? 'incident' : 'summary') : undefined}
        />
      </section>

      <div className="command-layout">
        <div className="command-layout__main">
          <section className="command-panel command-priority" data-tour="command-priorities">
            <PanelHeader
              eyebrow={t('Now')}
              title={t('Needs immediate action')}
              detail={priorityCount
                ? tn('{count} item is waiting for you.', '{count} items are waiting for you.', priorityCount)
                : allPriorityFeedsAvailable
                  ? t('No immediate actions need your attention.')
                  : t('Some action feeds are unavailable.')}
            />

            {isLoading && !snapshot.generatedAt ? (
              <div className="command-skeleton-list" role="status" aria-live="polite" aria-busy="true" aria-label={t('Loading command data')}>
                <span /><span /><span />
              </div>
            ) : priorityCount === 0 && allPriorityFeedsAvailable ? (
              <div className="command-empty command-empty--success">
                <FiCheckCircle aria-hidden="true" />
                <div><strong>{t('All caught up')}</strong><span>{t('No immediate actions need your attention.')}</span></div>
              </div>
            ) : priorityCount === 0 ? (
              <div className="command-empty">
                <FiAlertTriangle aria-hidden="true" />
                <span>{t('Some action feeds are unavailable.')}</span>
              </div>
            ) : (
              <div className="command-action-list">
                {displayedAlarms.map((alarm) => (
                  <button type="button" className="command-action command-action--danger" key={alarm.id} onClick={onOpenAlarms}>
                    <span className="command-action__icon"><FiAlertTriangle aria-hidden="true" /></span>
                    <span className="command-action__copy">
                      <span className="command-action__topline">
                        <strong>{alarm.title?.trim() || t('Active alarm')}</strong>
                        <span className="command-action__time">{formatRelative(alarm.triggered_at)}</span>
                      </span>
                      <span>{alarm.profiles?.full_name || t('Unknown guard')} · {alarm.sites?.name || t('Unknown site')}</span>
                    </span>
                    <span className="command-action__cta">{t('Respond')}<FiChevronRight aria-hidden="true" /></span>
                  </button>
                ))}

                {displayedNoShows.map((shift) => (
                  <button
                    type="button"
                    className="command-action command-action--danger"
                    key={shift.shiftId}
                    onClick={() => onOpenDutyBoard({ siteId: shift.siteId, date: shift.slotDate, dutyId: shift.dutyId })}
                  >
                    <span className="command-action__icon"><FiClock aria-hidden="true" /></span>
                    <span className="command-action__copy">
                      <span className="command-action__topline">
                        <strong>{t('{name} did not start their shift', { name: shift.guardName || t('Unknown guard') })}</strong>
                        <span className="command-action__time">{formatDate(shift.slotDate)}</span>
                      </span>
                      <span>{shift.siteName || t('Unknown site')} · {formatMinute(shift.startMinute)}–{formatMinute(shift.endMinute)}</span>
                    </span>
                    <span className="command-action__cta">{t('Open Duty Board')}<FiChevronRight aria-hidden="true" /></span>
                  </button>
                ))}

                {displayedLeave.map((request) => (
                  <button type="button" className="command-action command-action--warning" key={request.id} onClick={onOpenLeave}>
                    <span className="command-action__icon"><FiUserCheck aria-hidden="true" /></span>
                    <span className="command-action__copy">
                      <span className="command-action__topline">
                        <strong>{t('Leave request from {name}', { name: request.guard_name })}</strong>
                        <span className="command-action__time">{formatRelative(request.created_at)}</span>
                      </span>
                      <span>{formatDate(request.start_date)} – {formatDate(request.end_date)} · {t(request.leave_type === 'annual' ? 'Annual leave' : request.leave_type === 'sick' ? 'Sick leave' : request.leave_type === 'unpaid' ? 'Unpaid leave' : 'Other leave')}</span>
                    </span>
                    <span className="command-action__cta">{t('Review')}<FiChevronRight aria-hidden="true" /></span>
                  </button>
                ))}

                {Boolean(coverageIssueCount && coverageGaps[0]) && (
                  <button
                    type="button"
                    className="command-action command-action--warning"
                    onClick={() => onOpenDutyBoard({
                      siteId: coverageGaps[0].siteId,
                      date: coverageGaps[0].slotDate,
                      dutyId: coverageGaps[0].dutyId,
                    })}
                  >
                    <span className="command-action__icon"><FiCalendar aria-hidden="true" /></span>
                    <span className="command-action__copy">
                      <span className="command-action__topline">
                        <strong>{tn('{count} coverage issue', '{count} coverage issues', coverageIssueCount ?? 0)}</strong>
                      </span>
                      <span>{tn('{count} open position', '{count} open positions', openPositions ?? 0)}</span>
                    </span>
                    <span className="command-action__cta">{t('Review coverage')}<FiChevronRight aria-hidden="true" /></span>
                  </button>
                )}

                {Boolean(licenceWarningCount && snapshot.licenceWarnings[0]) && (
                  <button type="button" className="command-action command-action--warning" onClick={() => onOpenLicence(snapshot.licenceWarnings[0].id)}>
                    <span className="command-action__icon"><FiShield aria-hidden="true" /></span>
                    <span className="command-action__copy">
                      <span className="command-action__topline">
                        <strong>{tn('{count} licence warning', '{count} licence warnings', licenceWarningCount ?? 0)}</strong>
                      </span>
                      <span>{t('Expired or due within 30 days')}</span>
                    </span>
                    <span className="command-action__cta">{t('Review')}<FiChevronRight aria-hidden="true" /></span>
                  </button>
                )}

                {Boolean(reportReviewCount) && (
                  <button
                    type="button"
                    className="command-action command-action--info"
                    onClick={() => onOpenReports(snapshot.incidentReports.statusCounts.open ? 'incident' : 'summary')}
                  >
                    <span className="command-action__icon"><FiFileText aria-hidden="true" /></span>
                    <span className="command-action__copy">
                      <span className="command-action__topline">
                        <strong>{tn('{count} report needs review', '{count} reports need review', reportReviewCount ?? 0)}</strong>
                      </span>
                      <span>{t('New and updated field reports')}</span>
                    </span>
                    <span className="command-action__cta">{t('Review')}<FiChevronRight aria-hidden="true" /></span>
                  </button>
                )}

                {Boolean(notificationFeedAvailable && unreadCount && firstUnreadNotification) && (
                  <button
                    type="button"
                    className="command-action command-action--info"
                    aria-label={t('Open first unread update')}
                    onClick={() => openNotification(firstUnreadNotification!)}
                  >
                    <span className="command-action__icon"><FiBell aria-hidden="true" /></span>
                    <span className="command-action__copy">
                      <span className="command-action__topline">
                        <strong>{tn('{count} unread update', '{count} unread updates', unreadCount)}</strong>
                      </span>
                      <span>{firstUnreadNotification?.title}</span>
                    </span>
                    <span className="command-action__cta">{t('Open||action')}<FiChevronRight aria-hidden="true" /></span>
                  </button>
                )}
              </div>
            )}
          </section>

          <section className="command-panel" data-tour="command-coverage">
            <PanelHeader
              eyebrow={t('Next 14 days')}
              title={t('Coverage Horizon')}
              detail={t('The next uncovered or unpublished duties that need attention.')}
              actionLabel={coverageGaps[0] ? t('Open Duty Board') : undefined}
              onAction={coverageGaps[0] ? () => onOpenDutyBoard({ siteId: coverageGaps[0].siteId, date: coverageGaps[0].slotDate, dutyId: coverageGaps[0].dutyId }) : undefined}
            />
            {isLoading && !operations ? (
              <div className="command-skeleton-grid" role="status" aria-live="polite" aria-busy="true" aria-label={t('Loading coverage data')}><span /><span /><span /></div>
            ) : !operationsAvailable ? (
              <div className="command-empty"><FiAlertTriangle aria-hidden="true" /><span>{t('Data unavailable')}</span></div>
            ) : coverageGaps.length === 0 ? (
              <div className="command-empty">
                <FiCheckCircle aria-hidden="true" />
                <span>{t('No uncovered or unpublished duties are visible in the next 14 days.')}</span>
              </div>
            ) : (
              <div className="command-coverage-list">
                {coverageGaps.map((gap) => (
                  <button
                    type="button"
                    className="command-coverage-card"
                    key={gap.dutyId}
                    onClick={() => onOpenDutyBoard({ siteId: gap.siteId, date: gap.slotDate, dutyId: gap.dutyId })}
                  >
                    <span className="command-coverage-card__date">
                      <strong>{formatDate(gap.occurrenceDate || gap.slotDate, { weekday: 'short' })}</strong>
                      <span>{formatMinute(gap.startMinute)}–{formatMinute(gap.endMinute)}</span>
                    </span>
                    <span className="command-coverage-card__copy">
                      <strong>{getDutyName(gap.dutyName, gap.dutyKind)}</strong>
                      <span><FiMapPin aria-hidden="true" />{gap.siteName || t('Unknown site')}</span>
                    </span>
                    <span className="command-coverage-card__badges">
                      {!gap.isPublished && (
                        <span className="command-coverage-card__badge command-coverage-card__badge--unpublished">{t('Unpublished')}</span>
                      )}
                      {gap.openPositions > 0 && (
                        <span className="command-coverage-card__badge">{t('{count} open', { count: gap.openPositions })}</span>
                      )}
                    </span>
                    <FiChevronRight className="command-row__chevron" aria-hidden="true" />
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="command-panel command-reports" data-tour="command-reports">
            <PanelHeader
              eyebrow={t('Field reporting')}
              title={t('New and updated reports')}
              detail={t('Recent incident and summary reports, ordered by their latest update.')}
            />
            {!reportsAvailable ? (
              <div className="command-empty"><FiAlertTriangle aria-hidden="true" /><span>{t('Data unavailable')}</span></div>
            ) : <div className="command-report-columns">
              <div className="command-report-column">
                <div className="command-report-column__header">
                  <div><FiAlertTriangle aria-hidden="true" /><strong>{t('Incidents')}</strong></div>
                  <button type="button" onClick={() => onOpenReports('incident')}>{t('View all')}</button>
                </div>
                {snapshot.incidentReports.reports.length
                  ? snapshot.incidentReports.reports.map((report) => renderReport(report, 'incident'))
                  : <div className="command-mini-empty">{t('No recent incident reports')}</div>}
              </div>
              <div className="command-report-column">
                <div className="command-report-column__header">
                  <div><FiFileText aria-hidden="true" /><strong>{t('Summaries')}</strong></div>
                  <button type="button" onClick={() => onOpenReports('summary')}>{t('View all')}</button>
                </div>
                {snapshot.summaryReports.reports.length
                  ? snapshot.summaryReports.reports.map((report) => renderReport(report, 'summary'))
                  : <div className="command-mini-empty">{t('No recent summary reports')}</div>}
              </div>
            </div>}
          </section>
        </div>

        <aside className="command-layout__side">
          <section className="command-panel" data-tour="command-notifications">
            <PanelHeader
              eyebrow={t('Requests & updates')}
              title={t('Notification centre')}
              detail={!notificationsReady
                ? t('Loading notifications')
                : !notificationsAvailable
                  ? t('Data unavailable')
                  : tn('{count} unread update', '{count} unread updates', unreadCount)}
              actionLabel={notificationFeedAvailable && unreadCount ? t('Mark all as read') : undefined}
              onAction={notificationFeedAvailable && unreadCount
                ? () => {
                  if (onMarkAllNotificationsRead) void onMarkAllNotificationsRead();
                  else void onMarkNotificationRead(notifications.filter((item) => !item.read_at).map((item) => item.id));
                }
                : undefined}
            />
            {leaveAvailable && pendingLeave.length > 0 && (
              <button type="button" className="command-leave-summary" onClick={onOpenLeave}>
                <span className="command-leave-summary__icon"><FiUserCheck aria-hidden="true" /></span>
                <span><strong>{tn('{count} leave request', '{count} leave requests', pendingLeave.length)}</strong><small>{t('Awaiting your review')}</small></span>
                <FiChevronRight aria-hidden="true" />
              </button>
            )}
            <div className="command-notification-list">
              {!notificationsReady ? (
                <div className="command-mini-empty" role="status" aria-live="polite">{t('Loading notifications')}</div>
              ) : !notificationsAvailable ? (
                <div className="command-mini-empty">{t('Data unavailable')}</div>
              ) : recentNotifications.length === 0 ? (
                <div className="command-mini-empty">{t('No notifications yet')}</div>
              ) : recentNotifications.map((notification) => (
                <button
                  type="button"
                  className={`command-notification ${notification.read_at ? '' : 'command-notification--unread'}`}
                  key={notification.id}
                  onClick={() => openNotification(notification)}
                >
                  <span className="command-notification__dot" aria-hidden="true" />
                  {!notification.read_at && <span className="command-sr-only">{t('Unread')}</span>}
                  <span className="command-notification__copy">
                    <strong>{notification.title}</strong>
                    <span>{notification.body}</span>
                    <small>{formatRelative(notification.created_at)}</small>
                  </span>
                  <FiChevronRight className="command-row__chevron" aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>

          <section className="command-panel">
            <PanelHeader
              eyebrow={t('Compliance')}
              title={t('Licence warnings')}
              detail={t('Approved licences that are expired or expire within 30 days.')}
            />
            <div className="command-licence-list">
              {!licencesAvailable ? (
                <div className="command-empty command-empty--compact">
                  <FiAlertTriangle aria-hidden="true" /><span>{t('Data unavailable')}</span>
                </div>
              ) : snapshot.licenceWarnings.length === 0 ? (
                <div className="command-empty command-empty--compact">
                  <FiShield aria-hidden="true" /><span>{t('No licence expiry warnings')}</span>
                </div>
              ) : snapshot.licenceWarnings.map((licence) => (
                <button type="button" className="command-licence" key={licence.id} onClick={() => onOpenLicence(licence.id)}>
                  <span className={`command-licence__icon ${licence.daysUntilExpiry < 0 ? 'command-licence__icon--expired' : ''}`}>
                    <FiShield aria-hidden="true" />
                  </span>
                  <span className="command-licence__copy">
                    <strong>{licence.guardName || t('Unknown guard')}</strong>
                    <span>{licence.licenceTypeName || t('Security licence')}</span>
                    <small className={licence.daysUntilExpiry < 0 ? 'command-text--danger' : ''}>
                      {licence.daysUntilExpiry < 0
                        ? t('Expired {date}', { date: formatDate(licence.expiryDate) })
                        : t('Expires {date}', { date: formatDate(licence.expiryDate) })}
                    </small>
                  </span>
                  <FiChevronRight className="command-row__chevron" aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>

          <section className="command-panel command-pulse">
            <PanelHeader eyebrow={t('Live operations')} title={t('Operational pulse')} />
            <div className="command-pulse__grid">
              <button type="button" onClick={onOpenLiveView}>
                <FiActivity aria-hidden="true" /><strong>{operationsAvailable && operations?.liveAvailable !== false ? operations?.live.liveTrackedGuards ?? '—' : '—'}</strong><span>{t('Live guards')}</span>
              </button>
              <button type="button" onClick={onOpenLiveView}>
                <FiUsers aria-hidden="true" /><strong>{operationsAvailable ? operations?.live.activeExecutions ?? 0 : '—'}</strong><span>{t('Active shifts')}</span>
              </button>
              <button type="button" onClick={onOpenLiveView} className={operations?.live.lowBatteryCount ? 'command-pulse__warning' : ''}>
                <FiBattery aria-hidden="true" /><strong>{operationsAvailable && operations?.liveAvailable !== false ? operations?.live.lowBatteryCount ?? '—' : '—'}</strong><span>{t('Low battery')}</span>
              </button>
              <button type="button" onClick={onOpenLiveView} className={operations?.live.overrunningExecutions ? 'command-pulse__warning' : ''}>
                <FiClock aria-hidden="true" /><strong>{operationsAvailable ? operations?.live.overrunningExecutions ?? 0 : '—'}</strong><span>{t('Overrunning')}</span>
              </button>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
